import { ref, watch, computed } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import Vue from 'vue'
import axios from 'axios'
import { checkRole } from '@/auth/utils'
import apiEndpoints from '@/constants/apiEndpoints'
import router from '../../../router'

export default function useActivityLogList() {
  const toast = useToast()
  const refActivityLogListTable = ref(null)
  const loading = ref(false)

  // Use the same role-check helper the rest of the app relies on (sidebar,
  // route guards) instead of reading the userData cookie directly - that
  // direct read missed the fallback paths checkRole() already handles
  // (older cookie shapes, the separate 'UserRole' cookie, etc), which is why
  // the super-admin-only user filter/column could silently fail to show up.
  const isSuperAdmin = checkRole(['super-admin'])

  const tableColumns = [
    { key: 'time', sortable: false, label: 'Time' },
    ...(isSuperAdmin ? [{ key: 'user', sortable: false, label: 'User' }] : []),
    { key: 'module', sortable: false, label: 'Module' },
    { key: 'action', sortable: false, label: 'Action' },
    { key: 'description', sortable: false, label: 'Description' },
    { key: 'target', sortable: false, label: 'Target ID' },
    { key: 'ip', sortable: false, label: 'IP Address' },
  ]

  const perPage = ref(10)
  const totalActivityLogs = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  // Free-text search (partial match on module/description/target id) - NOT
  // an exact match on the module slug. Kept the name `moduleFilter` out of
  // the composable's public surface to avoid a churny rename; it's sent to
  // the backend as `search`, which does a LIKE '%...%' match server-side.
  const moduleFilter = ref('')
  const actionFilter = ref(null)
  const userFilter = ref(null)

  const dataMeta = computed(() => {
    const localItemsCount = refActivityLogListTable.value ? refActivityLogListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalActivityLogs.value,
    }
  })

  const refetchData = () => {
    if (refActivityLogListTable.value) {
      refActivityLogListTable.value.refresh()
    }
  }

  watch([currentPage, perPage, moduleFilter, actionFilter, userFilter], () => {
    refetchData()
  })

  const fetchActivityLogs = (ctx, callback) => {
    loading.value = true
    const to = perPage.value * (currentPage.value - 1)
    const userToken = Vue.$cookies.get('userToken')
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userToken}`,
    }

    const params = {
      limit: perPage.value,
      offset: to,
    }

    if (moduleFilter.value) {
      params.search = moduleFilter.value
    }

    if (actionFilter.value) {
      params.action = actionFilter.value
    }

    if (isSuperAdmin && userFilter.value) {
      params.user_id = userFilter.value
    }

    axios
      .get(`${process.env.VUE_APP_API_URL}${apiEndpoints.API_GET_ACTIVITY_LOG}`, {
        headers,
        params,
      })
      .then(response => {
        if (response.data && response.data.data !== null) {
          totalActivityLogs.value = response.data.data_count || 0
          callback(response.data.data)
        } else {
          callback([])
        }
        loading.value = false
      })
      .catch(e => {
        loading.value = false
        console.error(e)
        if (e.message === 'Request failed with status code 401') {
          router.push({ name: 'auth-login' })
        } else {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Error fetching data list',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        }
        callback([])
      })
  }

  const fetchUsersForFilter = () => {
    const userToken = Vue.$cookies.get('userToken')
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userToken}`,
    }

    return axios
      .get(`${process.env.VUE_APP_API_URL}${apiEndpoints.API_GET_USER}`, {
        headers,
        params: {
          limit: 1000,
          offset: 0,
          order_by: 'user_fullname',
          order_sort: 'ASC',
        },
      })
      .then(response => {
        if (response.data && response.data.data) {
          return response.data.data.map(user => ({
            label: user.user_fullname || user.user_username || `User #${user.user_id}`,
            value: user.user_id,
          }))
        }
        return []
      })
      .catch(e => {
        console.error(e)
        return []
      })
  }

  const titleizeModule = value => {
    if (!value) return '-'
    return value
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  const resolveActionVariant = action => {
    if (action === 'create') return 'success'
    if (action === 'update') return 'info'
    if (action === 'delete') return 'danger'
    if (action === 'confirm') return 'warning'
    return 'secondary'
  }

  const resolveActionLabel = action => {
    if (!action) return '-'
    return action.charAt(0).toUpperCase() + action.slice(1)
  }

  return {
    fetchActivityLogs,
    fetchUsersForFilter,
    loading,
    tableColumns,
    perPage,
    currentPage,
    totalActivityLogs,
    dataMeta,
    perPageOptions,
    moduleFilter,
    actionFilter,
    userFilter,
    refActivityLogListTable,
    refetchData,
    isSuperAdmin,
    titleizeModule,
    resolveActionVariant,
    resolveActionLabel,
  }
}
